// Razorpay Standard Checkout helper.
// Lazily injects the Razorpay JS script (https://checkout.razorpay.com/v1/checkout.js)
// and opens the checkout modal. Resolves with payment details on success,
// rejects on dismiss / failure.

const RAZORPAY_SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js';

const loadRazorpayScript = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Razorpay')));
      return;
    }
    const s = document.createElement('script');
    s.src = RAZORPAY_SCRIPT;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Razorpay'));
    document.body.appendChild(s);
  });

/**
 * Open Razorpay checkout with the given order.
 *
 * @param {Object} args
 * @param {string} args.orderId          Razorpay order id (order_xxx)
 * @param {number} args.amount           Amount in paise
 * @param {string} args.keyId            Razorpay key id (REACT_APP_RAZORPAY_KEY_ID)
 * @param {Object} args.prefill          { name, email, contact }
 * @param {string} [args.description]    Modal description
 * @param {string} [args.themeColor]     Hex color (default red)
 * @returns {Promise<{razorpay_order_id, razorpay_payment_id, razorpay_signature}>}
 */
export const openRazorpayCheckout = async (args) => {
  await loadRazorpayScript();
  const keyId = args.keyId || process.env.REACT_APP_RAZORPAY_KEY_ID;
  if (!keyId) throw new Error('Razorpay key not configured');

  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: keyId,
      amount: args.amount,
      currency: 'INR',
      name: 'Elite Travo',
      description: args.description || 'Booking payment',
      order_id: args.orderId,
      prefill: {
        name: args.prefill?.name || '',
        email: args.prefill?.email || '',
        contact: args.prefill?.contact || '',
      },
      theme: { color: args.themeColor || '#dc2626' },
      handler: (response) => {
        resolve({
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        });
      },
      modal: {
        ondismiss: () => reject(Object.assign(new Error('Payment cancelled'), { code: 'CANCELLED' })),
      },
    });
    rzp.on('payment.failed', (resp) => {
      reject(Object.assign(new Error(resp.error?.description || 'Payment failed'), {
        code: 'FAILED',
        details: resp.error,
      }));
    });
    rzp.open();
  });
};
